<template>
  <v-card class="chatservice-profile-card d-flex flex-column fill-height" data-testid="customer-profile-panel" :data-state="state">
    <v-card-title class="chatservice-profile-header d-flex align-center" data-testid="profile-header">
      <span class="text-h6 flex-grow-1">{{ $t('_customerProfile') }}</span>
      <v-btn v-if="dismissible" icon="mdi-close" variant="text" size="small" :aria-label="$t('close')" data-testid="profile-dismiss-btn" @click="$emit('dismiss')" />
    </v-card-title>

    <v-card-text class="chatservice-profile-body flex-grow-1">
      <!-- S0 empty -->
      <div v-if="state === 'empty'" class="chatservice-profile-empty">
        <v-icon size="48" color="grey">mdi-account-search-outline</v-icon>
        <div>{{ $t('_selectChatToSeeProfile') }}</div>
      </div>

      <!-- S1 loading -->
      <v-skeleton-loader v-else-if="state === 'loading'" type="list-item-avatar-two-line, article, chip@4" />

      <!-- S2 error -->
      <div v-else-if="state === 'error'" class="chatservice-profile-empty">
        <v-icon size="48" color="grey">mdi-alert-circle-outline</v-icon>
        <div>{{ $t('_profileLoadFailed') }}</div>
        <v-btn variant="text" color="primary" data-testid="profile-retry-btn" @click="$emit('retry')">{{ $t('_retry') }}</v-btn>
      </div>

      <!-- S3 ready / S4 closed -->
      <template v-else>
        <!-- 5.1 Customer Info -->
        <v-card variant="outlined" class="mb-3">
          <v-card-title class="text-subtitle-1">
            <v-icon start>mdi-account-outline</v-icon>
            {{ $t('_customerInfo') }}
          </v-card-title>
          <v-card-text>
            <div class="d-flex align-center ga-2 mb-3">
              <v-avatar size="40" :color="getColor(info.socialType)" data-testid="profile-avatar">
                <v-img v-if="info.pictureUrl && !avatarError" :src="info.pictureUrl" :alt="info.displayName" @error="avatarError = true" />
                <span v-else class="text-white">{{ initial }}</span>
              </v-avatar>
              <span class="text-body-1 flex-grow-1 chatservice-profile-name" data-testid="profile-name">{{ info.displayName }}</span>
              <v-chip v-if="isClosed" size="small" color="grey" data-testid="profile-closed-chip">{{ $t('_closedChip') }}</v-chip>
              <v-chip v-else size="small" :color="adminReply ? 'primary' : 'grey'" data-testid="profile-mode-chip" :data-mode="adminReply ? 'admin' : 'bot'">
                {{ adminReply ? $t('admin') : $t('bot') }}
              </v-chip>
            </div>

            <div class="chatservice-profile-field">
              <div class="chatservice-profile-label">{{ $t('_customerChannel') }}</div>
              <div class="chatservice-profile-value d-flex align-center ga-1">
                <img v-if="getLogo(info.socialType)" :src="getLogo(info.socialType)" width="16" height="16" :alt="info.socialType" />
                <span data-testid="profile-channel">{{ info.socialType }}</span>
              </div>
            </div>

            <div v-if="info.shopName" class="chatservice-profile-field">
              <div class="chatservice-profile-label">{{ $t('_shopName') }}</div>
              <div class="chatservice-profile-value" data-testid="profile-shop">{{ info.shopName }}</div>
            </div>

            <div class="chatservice-profile-field">
              <div class="chatservice-profile-label">{{ $t('_sessionId') }}</div>
              <div class="chatservice-profile-value">
                <button v-if="info.sessionId" type="button" class="chatservice-copyable" data-testid="profile-session-id" @click="copyText(info.sessionId, '_sessionIdCopied')">
                  <span class="chatservice-profile-mono">{{ info.sessionId }}</span>
                  <v-icon size="14">mdi-content-copy</v-icon>
                  <v-tooltip activator="parent" location="top">{{ $t('_Copy') }}</v-tooltip>
                </button>
                <span v-else data-testid="profile-session-id" data-empty="true">—</span>
              </div>
            </div>

            <div v-if="isCommerce" class="chatservice-profile-field">
              <div class="chatservice-profile-label">{{ $t('_conversationId') }}</div>
              <div class="chatservice-profile-value">
                <button
                  v-if="info.conversationId"
                  type="button"
                  class="chatservice-copyable"
                  data-testid="profile-conversation-id"
                  @click="copyText(info.conversationId, '_conversationIdCopied')"
                >
                  <span class="chatservice-profile-mono">{{ info.conversationId }}</span>
                  <v-icon size="14">mdi-content-copy</v-icon>
                  <v-tooltip activator="parent" location="top">{{ $t('_Copy') }}</v-tooltip>
                </button>
                <v-tooltip v-else location="top">
                  <template #activator="{ props }">
                    <span v-bind="props" data-testid="profile-conversation-id" data-empty="true">—</span>
                  </template>
                  {{ $t('_conversationIdMissingHint') }}
                </v-tooltip>
              </div>
            </div>

            <div class="chatservice-profile-field">
              <div class="chatservice-profile-label d-flex align-center ga-1">
                {{ $t('_activeTime') }}
                <v-tooltip location="top">
                  <template #activator="{ props }">
                    <v-icon v-bind="props" size="14">mdi-information-outline</v-icon>
                  </template>
                  {{ $t('_activeTimeHint') }}
                </v-tooltip>
              </div>
              <div class="chatservice-profile-value" data-testid="profile-active-time">{{ formatActiveTime(info.activeMinutes) }}</div>
            </div>
          </v-card-text>
        </v-card>

        <!-- 5.2 Interaction History -->
        <v-card variant="outlined" class="mb-3">
          <v-card-title class="text-subtitle-1">
            <v-icon start>mdi-history</v-icon>
            {{ $t('_interactionHistory') }}
          </v-card-title>
          <v-card-text>
            <v-timeline v-if="history.length" density="compact" side="end" truncate-line="both" align="start" data-testid="profile-history">
              <v-timeline-item v-for="h in history" :key="h.topicId" :dot-color="getColor(h.socialType)" size="x-small">
                <div
                  class="chatservice-profile-history-item"
                  :class="{ 'is-current': h.isCurrent, 'is-closed': h.activeFlag === false }"
                  :style="h.isCurrent ? { '--theme-color': themeColor } : null"
                  :data-testid="'profile-history-item-' + h.topicId"
                  :data-current="h.isCurrent ? 'true' : null"
                  :data-closed="h.activeFlag === false ? 'true' : null"
                >
                  <div class="text-body-2 d-flex align-center ga-1 flex-wrap">
                    <span :data-testid="'profile-history-date-' + h.topicId">{{ formatDate(h.createDate) }}</span>
                    <v-chip v-if="h.isCurrent" size="x-small" variant="tonal">{{ $t('_currentChat') }}</v-chip>
                  </div>
                  <div class="text-caption text-medium-emphasis d-flex align-center ga-1">
                    <img v-if="getLogo(h.socialType)" :src="getLogo(h.socialType)" width="14" height="14" :alt="h.socialType" />
                    <span>{{ h.socialType }} · {{ truncate(h.latestMsg) }}</span>
                  </div>
                </div>
              </v-timeline-item>
            </v-timeline>
            <div v-else class="text-caption text-medium-emphasis">{{ $t('noDataAvailable') }}</div>
          </v-card-text>
        </v-card>

        <!-- 5.3 Previous Inquiry Topics -->
        <v-card variant="outlined">
          <v-card-title class="text-subtitle-1">
            <v-icon start>mdi-tag-multiple-outline</v-icon>
            {{ $t('_previousInquiryTopics') }}
          </v-card-title>
          <v-card-text>
            <div v-if="topics.length" class="d-flex flex-wrap ga-1" data-testid="profile-topics">
              <v-chip v-for="(t, i) in topics" :key="t" size="small" variant="tonal" :data-testid="'profile-topic-chip-' + i">{{ t }}</v-chip>
            </div>
            <div v-else class="text-medium-emphasis text-caption" data-testid="profile-topics-empty">{{ $t('_noInquiryTopics') }}</div>
          </v-card-text>
        </v-card>
      </template>
    </v-card-text>

    <!-- 5.4 / 5.5 footer -->
    <v-card-actions v-if="showFooter" class="chatservice-profile-footer flex-column">
      <v-tooltip location="top" :disabled="!isClosed">
        <template #activator="{ props }">
          <div v-bind="props" class="w-100 d-flex flex-column ga-2">
            <v-btn
              block
              variant="outlined"
              color="primary"
              :disabled="actionsLocked"
              :loading="transferBusy"
              data-testid="profile-transfer-btn"
              :data-admin-reply="adminReply ? 'true' : 'false'"
              @click="actionsLocked ? undefined : onTransfer()"
            >
              {{ adminReply ? $t('_returnToBot') : $t('_transferToAgent') }}
            </v-btn>
            <v-btn block variant="outlined" color="#C32E0E" :disabled="actionsLocked" :loading="closeBusy" data-testid="profile-close-btn" @click="actionsLocked ? undefined : onClose()">
              {{ $t('_closeChat') }}
            </v-btn>
          </div>
        </template>
        {{ $t('_chatAlreadyClosed') }}
      </v-tooltip>
    </v-card-actions>
  </v-card>
</template>

<script>
// S14-P4 Wave 5 — Customer Profile panel (column ⑤ / drawer / bottom sheet) for /ChatService
// Pure presentational: no API / SignalR / inject / asset imports — parent owns loading and actions (D12).
const COMMERCE_TYPES = ['TikTok', 'Shopee', 'Lazada']; // RC2 / T2 — channels that expose a provider conversation id

export default {
  name: 'CustomerProfilePanel',
  props: {
    topicId: { type: Number, default: 0 }, // 0 = nothing selected (S0)
    profile: { type: Object, default: null }, // ResponseCustomerProfile { customerInfo, interactionHistory[], inquiryTopics[] }
    loading: { type: Boolean, default: false },
    error: { type: String, default: null },
    adminReply: { type: Boolean, default: false },
    transferBusy: { type: Boolean, default: false },
    closeBusy: { type: Boolean, default: false },
    sendBusy: { type: Boolean, default: false }, // U5 — parent statusSendMessage
    closed: { type: Boolean, default: false }, // S4 from parent
    dismissible: { type: Boolean, default: false }, // U3 — drawer / bottom sheet
    themeColor: { type: String, default: '' }, // parent selectedColor
    getLogo: { type: Function, required: true }, // (socialType) => src | null
    getColor: { type: Function, required: true }, // (socialType) => hex
  },
  emits: ['transfer', 'close', 'retry', 'dismiss'],
  data() {
    return {
      avatarError: false, // S6
    };
  },
  computed: {
    info() {
      return this.profile?.customerInfo || null;
    },
    history() {
      return this.profile?.interactionHistory || [];
    },
    topics() {
      return this.profile?.inquiryTopics || [];
    },
    isClosed() {
      return this.closed || this.history.find((h) => h.isCurrent)?.activeFlag === false;
    },
    // U5 — single predicate used by both :disabled and @click of 5.4 / 5.5
    actionsLocked() {
      return this.transferBusy || this.closeBusy || this.sendBusy || this.isClosed;
    },
    state() {
      if (!this.topicId) return 'empty';
      if (this.loading) return 'loading';
      if (this.error) return 'error';
      if (this.profile && this.info) return this.isClosed ? 'closed' : 'ready';
      return 'empty';
    },
    // S2 keeps the footer: transfer/close rely on the adminReply prop, not on the profile payload
    showFooter() {
      return this.topicId > 0 && !this.loading;
    },
    isCommerce() {
      return COMMERCE_TYPES.includes(this.info?.socialType);
    },
    initial() {
      return (this.info?.displayName || '').trim().charAt(0).toUpperCase();
    },
    dateLocale() {
      return this.$i18n.locale === 'th' ? 'th-TH' : 'en-GB'; // TS-3
    },
  },
  watch: {
    profile() {
      this.avatarError = false;
    },
  },
  methods: {
    formatActiveTime(min) {
      const value = Number(min) || 0;
      if (value < 60) return this.$t('_activeTimeMins', { min: value });
      return this.$t('_activeTimeHrMins', { h: Math.floor(value / 60), m: value % 60 });
    },
    formatDate(iso) {
      if (!iso) return '';
      // API ส่ง DateTime UTC ไม่มี offset → ต่อ 'Z' (pattern FlowList.vue) — ALL-W7-A TC-07b
      const s = String(iso);
      const d = new Date(s.includes('T') && !s.includes('Z') && !/[+-]\d{2}:\d{2}$/.test(s) ? s + 'Z' : s);
      if (isNaN(d.getTime())) return '';
      return d.toLocaleString(this.dateLocale, { dateStyle: 'medium', timeStyle: 'short' });
    },
    truncate(text, n = 60) {
      const value = text || '';
      return value.length > n ? value.slice(0, n) + '…' : value;
    },
    async copyText(value, i18nKey) {
      try {
        await navigator.clipboard.writeText(value);
        this.$swal.fire({ toast: true, position: 'top-end', icon: 'success', title: this.$t(i18nKey), timer: 1200, showConfirmButton: false });
      } catch (e) {
        // clipboard unavailable (non-secure context) — surface the raw value so the user can copy it manually
        this.$swal.fire({ toast: true, position: 'top-end', icon: 'warning', title: value, timer: 2500, showConfirmButton: false });
      }
    },
    onTransfer() {
      if (this.actionsLocked) return;
      this.$emit('transfer');
    },
    onClose() {
      if (this.actionsLocked) return;
      this.$emit('close');
    },
  },
};
</script>

<style scoped>
.chatservice-profile-card {
  border: 1px solid #e0e0e0;
  border-radius: 15px;
  box-shadow: none;
  min-height: 0;
}
.chatservice-profile-header {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #fff;
}
.chatservice-profile-body {
  overflow-y: auto;
  min-height: 0;
}
.chatservice-profile-footer {
  position: sticky;
  bottom: 0;
  background: #fff;
  border-top: 1px solid #e0e0e0;
}
.chatservice-profile-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 240px;
  text-align: center;
  color: #666;
}
.chatservice-profile-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.chatservice-profile-field {
  margin-bottom: 8px;
}
.chatservice-profile-label {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
}
.chatservice-profile-value {
  font-size: 14px;
  word-break: break-all;
}
.chatservice-profile-mono {
  font-family: monospace;
}
.chatservice-copyable {
  background: none;
  border: 0;
  padding: 0;
  font: inherit;
  color: inherit;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.chatservice-profile-history-item.is-current {
  background: #f5f5f5;
  background: color-mix(in srgb, var(--theme-color, #c32e0e) 10%, transparent);
  border-radius: 8px;
  padding: 4px 8px;
}
.chatservice-profile-history-item.is-closed {
  opacity: 0.6;
}
@media (max-width: 767px) {
  .chatservice-profile-footer .v-btn {
    height: 44px;
  }
}
</style>
